import axios from "axios";
import {
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
  getStoredAuthRole,
  isAccessTokenExpired,
  setTokens,
} from "./authStorage";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8001";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshPromise = null;
let onUnauthorized = null;
let onTokensRefreshed = null;

const AUTH_SKIP_PATHS = [
  "/admin/login",
  "/admin/refresh",
  "/partner/login",
  "/partner/refresh",
  "/public/",
];

function shouldSkipAuthRetry(url = "") {
  return AUTH_SKIP_PATHS.some((path) => url.includes(path));
}

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

export function setTokensRefreshedHandler(handler) {
  onTokensRefreshed = handler;
}

function applyAuthHeader(config, token = getAccessToken()) {
  config.headers = config.headers || {};

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    config.headers.authorization = `Bearer ${token}`;
    config.headers["x-access-token"] = token;
  } else {
    delete config.headers.Authorization;
    delete config.headers.authorization;
    delete config.headers["x-access-token"];
  }

  return config;
}

function refreshPath() {
  return getStoredAuthRole() === "partner_admin"
    ? "/partner/refresh"
    : "/admin/refresh";
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await axios.post(
    `${API_BASE_URL}${refreshPath()}`,
    { refreshToken },
    {
      headers: { "Content-Type": "application/json" },
    },
  );

  const payload = response.data?.data || response.data || {};
  const accessToken = setTokens({
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken || refreshToken,
  });

  api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

  if (onTokensRefreshed) {
    onTokensRefreshed({
      accessToken,
      refreshToken: payload.refreshToken || refreshToken,
    });
  }

  return accessToken;
}

export async function ensureValidAccessToken() {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (!accessToken || !refreshToken) {
    return accessToken;
  }

  if (!isAccessTokenExpired(accessToken)) {
    return accessToken;
  }

  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

/** Refresh once, sharing an in-flight refresh with other callers. */
export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function forceLogout() {
  clearAuthStorage();
  delete api.defaults.headers.common.Authorization;

  if (onUnauthorized) {
    onUnauthorized();
  }
}

api.interceptors.request.use(
  async (config) => {
    if (!shouldSkipAuthRetry(config.url || "")) {
      try {
        await ensureValidAccessToken();
      } catch {
        forceLogout();
        return Promise.reject(new Error("Session expired"));
      }
    }

    return applyAuthHeader(config);
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || "";

    if (
      !originalRequest ||
      originalRequest._retry ||
      status !== 401 ||
      shouldSkipAuthRetry(requestUrl)
    ) {
      if (status === 401 && !shouldSkipAuthRetry(requestUrl)) {
        forceLogout();
      }
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      forceLogout();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }

      const accessToken = await refreshPromise;
      return api(applyAuthHeader(originalRequest, accessToken));
    } catch (refreshError) {
      forceLogout();
      return Promise.reject(refreshError);
    }
  },
);

export default api;
